// Per-carrier route history sub-card (D3h). Renders as an inline
// expansion under the carrier display row when the user clicks its
// "Routes" toggle in LogisticsCarriersList. Read-only view -- to
// act on any of these routes (mark in-transit, edit stops, etc.)
// the user switches to the logisticsRoutes mode. Cross-mode
// deep-link is a deferred follow-on (would need the lead-workspace
// shell to expose a "switch to mode + select id" imperative).
//
// Sourced from useLogisticsCarrierRouteHistory which fetches routes
// filtered by the carrier_id (server-side filter is the D3b carrier_id
// param on /api/logi/routes) plus units (for the display
// labels via the shared unitLabel helper).
import {
  ROUTE_STATE_CHIP_CLASSES,
  ROUTE_STATE_LABELS,
  formatRouteScheduled,
  formatTimestamp,
  ghostBtnClass,
  unitLabel,
} from "./logisticsRoutesShared";
import type { useLogisticsCarrierRouteHistory } from "./useLogisticsCarrierRouteHistory";
import type { LogiCarrier, LogiRoute } from "@/lib/logistics";
import type { MfgUnit } from "@/lib/manufacturingClient";

type Props = {
  carrier: LogiCarrier;
  h: ReturnType<typeof useLogisticsCarrierRouteHistory>;
  onClose: () => void;
};

export function LogisticsCarriersRouteHistoryCard({ carrier, h, onClose }: Props) {
  return (
    <div className="mt-2 rounded-xl border border-indigo-200 bg-indigo-50/40 dark:border-indigo-800 dark:bg-indigo-950/20 p-3">
      <div className="flex flex-wrap items-center gap-2 justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
          Routes for {carrier.name}
          {h.routes.length > 0 ? (
            <span className="ml-2 font-normal text-gray-500 dark:text-zinc-400">
              ({h.routes.length} · newest first)
            </span>
          ) : null}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => void h.reload()}
            disabled={h.loading}
          >
            {h.loading ? "Loading…" : "Refresh"}
          </button>
          <button type="button" className={ghostBtnClass} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      {h.error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {h.error}
        </div>
      ) : null}
      {!h.error && h.loading && h.routes.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading routes…</div>
      ) : null}
      {!h.error && !h.loading && h.routes.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          No routes reference this carrier yet. Routes that pick this carrier on creation, or
          have it assigned later, will appear here.
        </div>
      ) : null}
      {h.routes.length > 0 ? (
        <ul className="divide-y divide-indigo-100 dark:divide-indigo-900/40">
          {h.routes.map((r) => (
            <RouteHistoryRow key={r.id} route={r} units={h.units} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function RouteHistoryRow({
  route,
  units,
}: {
  route: LogiRoute;
  units: MfgUnit[];
}) {
  const chipClass = ROUTE_STATE_CHIP_CLASSES[route.route_state] ?? "bg-gray-500 text-white";
  const scheduled = formatRouteScheduled(route);
  const completed = formatTimestamp(route.completed_at);
  return (
    <li className="py-2 flex flex-wrap items-start gap-2">
      <span
        className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${chipClass} shrink-0`}
      >
        {ROUTE_STATE_LABELS[route.route_state] ?? route.route_state}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-900 dark:text-zinc-100">
          <code>{unitLabel(units, route.unit_id)}</code>
        </div>
        {scheduled ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">Scheduled: {scheduled}</div>
        ) : null}
        {completed ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">Completed: {completed}</div>
        ) : null}
        {route.notes ? (
          <div className="text-xs text-gray-600 dark:text-zinc-400">{route.notes}</div>
        ) : null}
      </div>
    </li>
  );
}
